import {
	Component,
	forwardRef,
	AfterViewInit,
	ViewChild,
	Input,
} from '@angular/core';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormArray,
	FormGroup,
	FormBuilder,
} from '@angular/forms';
import { FieldWorkerLink } from 'src/shared/api';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { takeUntil, filter } from 'rxjs/operators';
import { fieldWorkerLinkFormConfig } from '../field-worker-link.component';
import { animateList } from 'src/shared/animations';
import { FieldWorkerLinkDialogComponent } from '../field-worker-link-dialog/field-worker-link-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbstractControlDataSource } from '../../abstract-control-data-source';

/*
	This table component is built using a form array to allow for easier editing.
	The important thing to remember is that the "parent" FormGroup is just a wrapper
	and has no actual value outside of giving us somewhere to store a FormArray. As of
	Angular 7 (and probably 8), there is no way to have a FormArray be the top level
	of a form.
*/

@Component({
	selector: 'rpshared-field-worker-link-list',
	templateUrl: './field-worker-link-list.component.html',
	styleUrls: ['./field-worker-link-list.component.scss'],
	animations: [animateList],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldWorkerLinkListComponent),
			multi: true,
		},
	],
})
export class FieldWorkerLinkListComponent extends SelfDestruct
	implements ControlValueAccessor, AfterViewInit {
	@Input() fieldWorkerLinkChoices: FieldWorkerLink[] = [];

	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	emptyColumns = ['empty'];
	resultColumns = ['name', 'employeeCode', 'edit', 'delete'];

	// Form Controls
	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<FieldWorkerLink>(this.array);

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: FieldWorkerLink[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(fieldWorkerLinkFormConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: FieldWorkerLink[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}
	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addFieldWorker() {
		this.dialog
			.open<
				FieldWorkerLinkDialogComponent,
				{ fieldWorkerLinkChoices: FieldWorkerLink[] },
				FormGroup
			>(FieldWorkerLinkDialogComponent, {
				width: '450px',
				data: {
					fieldWorkerLinkChoices: this.fieldWorkerLinkChoices,
				},
			})
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				this.array.insert(0, control);
			});
	}

	editFieldWorker(fieldWorkerLink: FieldWorkerLink) {
		this.dialog
			.open<
				FieldWorkerLinkDialogComponent,
				{
					fieldWorkerLink: FieldWorkerLink;
				},
				FormGroup
			>(FieldWorkerLinkDialogComponent, {
				width: '450px',
				data: {
					fieldWorkerLink: fieldWorkerLink
				},
			})
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				this.array.setControl(this.findFieldWorkerLinkIndex(fieldWorkerLink), control);
			});
	}

	findFieldWorkerLinkIndex(fieldWorkerLink: FieldWorkerLink){
		return this.dataSource.data.findIndex(fwl => fwl === fieldWorkerLink);
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}
}
